import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DirectionsIcon from '@material-ui/icons/Directions';
import EventIcon from '@material-ui/icons/Event';
import PersonIcon from '@material-ui/icons/Person';
import DriveEtaIcon from '@material-ui/icons/DriveEta';
import PeopleIcon from '@material-ui/icons/People';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  listItem: {
    padding: theme.spacing(1, 0),
  },
  listItemLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
  tripIcon: {
    fontSize: 60,
    marginBottom: theme.spacing(2),
  },
  tripHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  tripStats: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tripStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  tripStatValue: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  tripStatLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  infoCard: {
    height: '100%',
  },
  infoCardIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  infoCardTitle: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  infoCardContent: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  occupancyBar: {
    height: 20,
    borderRadius: 10,
    backgroundColor: theme.palette.grey[300],
    position: 'relative',
    overflow: 'hidden',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  occupancyFill: {
    height: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  occupancyText: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'white',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    lineHeight: '20px',
    textShadow: '0px 0px 2px rgba(0,0,0,0.5)',
  },
}));

const TripDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchTrip();
  }, [id]);

  const fetchTrip = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const tripDoc = doc(firestore, 'trips', id);
      const tripSnapshot = await getDoc(tripDoc);
      
      if (tripSnapshot.exists()) {
        setTrip({
          id: tripSnapshot.id,
          ...tripSnapshot.data()
        });
      } else {
        // Si le voyage n'existe pas, utiliser des données de démonstration
        setTrip({
          id: id,
          routeId: '1',
          routeName: 'Paris - Lyon',
          departureDate: '2025-04-15T08:00:00',
          arrivalDate: '2025-04-15T12:30:00',
          status: 'Scheduled',
          availableSeats: 45,
          bookedSeats: 32,
          price: 45.99,
          vehicleId: 'V001',
          vehicleName: 'Bus 101',
          driverId: 'D001',
          driverName: 'Jean Dupont',
          notes: 'Voyage régulier entre Paris et Lyon. Arrêt de 15 minutes à mi-parcours.',
          createdAt: new Date().toISOString(),
          bookings: [
            { id: 'B001', passengerName: 'Alice Martin', seats: 2, totalPrice: 91.98 },
            { id: 'B002', passengerName: 'Thomas Bernard', seats: 1, totalPrice: 45.99 },
            { id: 'B003', passengerName: 'Sophie Dubois', seats: 3, totalPrice: 137.97 }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching trip:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données du voyage',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/trips/${id}/edit`);
  };

  const handleCancel = () => {
    navigate(`/admin/trips/${id}/cancel`);
  };

  const handleConfirmCancel = async () => {
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const tripDoc = doc(firestore, 'trips', id);
      await updateDoc(tripDoc, {
        status: 'Cancelled',
        cancelledAt: new Date(),
        cancellationReason: 'Annulé par l\'administrateur'
      });
      
      setTrip({
        ...trip,
        status: 'Cancelled',
        cancelledAt: new Date().toISOString(),
        cancellationReason: 'Annulé par l\'administrateur'
      });
      
      setSnackbar({
        open: true,
        message: 'Voyage annulé avec succès',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error cancelling trip:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'annulation du voyage',
        severity: 'error'
      });
    } finally {
      setOpenCancelDialog(false);
    }
  };

  const handleCancelDialogOpen = () => {
    setOpenCancelDialog(true);
  };

  const handleCancelDialogClose = () => {
    setOpenCancelDialog(false);
  };

  const handleBack = () => {
    navigate('/admin/trips');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'primary';
      case 'Completed':
        return 'default';
      case 'Cancelled':
        return 'secondary';
      case 'InProgress':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'Programmé';
      case 'Completed':
        return 'Terminé';
      case 'Cancelled':
        return 'Annulé';
      case 'InProgress':
        return 'En cours';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const getOccupancyRate = (booked, available) => {
    return Math.round((booked / (booked + available)) * 100);
  };

  const getTripDuration = (departure, arrival) => {
    const departureTime = new Date(departure).getTime();
    const arrivalTime = new Date(arrival).getTime();
    const durationMs = arrivalTime - departureTime;
    
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Détails du voyage
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : trip ? (
        <>
          <Paper className={classes.paper}>
            <div className={classes.tripHeader}>
              <DirectionsIcon className={classes.tripIcon} color="primary" />
              <Typography variant="h5" component="h2">
                {trip.routeName}
              </Typography>
              <Chip 
                label={getStatusLabel(trip.status)} 
                color={getStatusColor(trip.status)} 
                className={classes.chip}
              />
            </div>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card className={classes.infoCard}>
                  <CardContent>
                    <EventIcon className={classes.infoCardIcon} />
                    <Typography className={classes.infoCardTitle}>
                      Départ
                    </Typography>
                    <Typography className={classes.infoCardContent}>
                      {formatDate(trip.departureDate)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card className={classes.infoCard}>
                  <CardContent>
                    <EventIcon className={classes.infoCardIcon} />
                    <Typography className={classes.infoCardTitle}>
                      Arrivée
                    </Typography>
                    <Typography className={classes.infoCardContent}>
                      {formatDate(trip.arrivalDate)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card className={classes.infoCard}>
                  <CardContent>
                    <DirectionsIcon className={classes.infoCardIcon} />
                    <Typography className={classes.infoCardTitle}>
                      Durée
                    </Typography>
                    <Typography className={classes.infoCardContent}>
                      {getTripDuration(trip.departureDate, trip.arrivalDate)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card className={classes.infoCard}>
                  <CardContent>
                    <DriveEtaIcon className={classes.infoCardIcon} />
                    <Typography className={classes.infoCardTitle}>
                      Véhicule
                    </Typography>
                    <Typography className={classes.infoCardContent}>
                      {trip.vehicleName}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card className={classes.infoCard}>
                  <CardContent>
                    <PersonIcon className={classes.infoCardIcon} />
                    <Typography className={classes.infoCardTitle}>
                      Chauffeur
                    </Typography>
                    <Typography className={classes.infoCardContent}>
                      {trip.driverName}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card className={classes.infoCard}>
                  <CardContent>
                    <AttachMoneyIcon className={classes.infoCardIcon} />
                    <Typography className={classes.infoCardTitle}>
                      Prix par passager
                    </Typography>
                    <Typography className={classes.infoCardContent}>
                      {formatPrice(trip.price)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <PeopleIcon className={classes.infoCardIcon} />
                    <Typography className={classes.infoCardTitle}>
                      Occupation
                    </Typography>
                    <div className={classes.occupancyBar}>
                      <div 
                        className={classes.occupancyFill} 
                        style={{ width: `${getOccupancyRate(trip.bookedSeats, trip.availableSeats)}%` }}
                      />
                      <div className={classes.occupancyText}>
                        {getOccupancyRate(trip.bookedSeats, trip.availableSeats)}%
                      </div>
                    </div>
                    <Typography variant="body1">
                      {trip.bookedSeats} sièges réservés sur {trip.bookedSeats + trip.availableSeats} disponibles
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {trip.notes && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="h6" className={classes.sectionTitle}>
                  Notes
                </Typography>
                <Typography variant="body1" paragraph>
                  {trip.notes}
                </Typography>
              </>
            )}
            
            {trip.bookings && trip.bookings.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="h6" className={classes.sectionTitle}>
                  Réservations ({trip.bookings.length})
                </Typography>
                <TableContainer component={Paper} style={{ marginTop: 16 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Passager</TableCell>
                        <TableCell>Sièges</TableCell>
                        <TableCell>Prix total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trip.bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell>{booking.id}</TableCell>
                          <TableCell>{booking.passengerName}</TableCell>
                          <TableCell>{booking.seats}</TableCell>
                          <TableCell>{formatPrice(booking.totalPrice)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {trip.status === 'Cancelled' && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="h6" className={classes.sectionTitle} style={{ color: 'red' }}>
                  Informations d'annulation
                </Typography>
                <Typography variant="body1">
                  <strong>Date d'annulation:</strong> {trip.cancelledAt ? formatDate(trip.cancelledAt) : 'Non spécifiée'}
                </Typography>
                <Typography variant="body1">
                  <strong>Raison:</strong> {trip.cancellationReason || 'Aucune raison spécifiée'}
                </Typography>
              </>
            )}
            
            <div className={classes.buttons}>
              <div>
                {trip.status === 'Scheduled' && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<DeleteIcon />}
                    onClick={handleCancelDialogOpen}
                  >
                    Annuler le voyage
                  </Button>
                )}
              </div>
              <div>
                {trip.status === 'Scheduled' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    Modifier
                  </Button>
                )}
              </div>
            </div>
          </Paper>
        </>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Voyage non trouvé
          </Typography>
        </Paper>
      )}
      
      {/* Dialog de confirmation d'annulation */}
      <Dialog
        open={openCancelDialog}
        onClose={handleCancelDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer l'annulation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir annuler le voyage {trip?.routeName} du {trip ? formatDate(trip.departureDate) : ''} ? 
            Cette action notifiera tous les passagers ayant réservé ce voyage.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} color="primary">
            Retour
          </Button>
          <Button onClick={handleConfirmCancel} color="secondary" autoFocus>
            Confirmer l'annulation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TripDetailPage;
